import React, { useEffect, useState, useRef } from "react";
import { reduxForm } from "redux-form";
import { removeNonNumericCharacters } from "../../../utils/utils";
import { generateLoanDescription } from "../misc/messaging-utils";
import AuthAchFields from "./field-components/general/auth-ach-fields.component";
import { AuthPaymentContainer } from "../misc/auth-payment-container.component";
import { AuthRecurringValidationDisplay } from "../misc/auth-recurring-validation-display.component";
import { RecurringDatePicker } from "./field-components/recurring-fields/recurring-date-picker.component";
import { RecurringFrequency } from "./field-components/recurring-fields/recurring-frequency.component";
import { AuthAmountField } from "./field-components/general/auth-amount-field.component";
import { AuthContinue } from "../misc/auth-continue.component";
import { AuthRestartButton } from "../misc/restart-button.component";
import { constructRecurringInitializationObject } from "../misc/recurring-utils";
import { recurringPaymentScheduledTodayMessage } from "../misc/messaging-utils";
import { usd } from "../../../utils/utils";
import moment from "moment";
import { formatOlbStyleAuthAmountValueFromBackend } from "../misc/auth-amount-utility";

const AuthPaymentRecurringAchClosedEndForm = (props) => {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const previousFrequency = useRef();

  const paymentValidated = props.authPayment.recurringValidation.IsValid;
  const hasSyncErrors = props.thisForm && props.thisForm.syncErrors;

  const values = (props.thisForm && props.thisForm.values) || {};
  const routingNumber = values.RoutingNumber ? values.RoutingNumber : "";
  const accountNumber = values.AccountNumber;
  const checking = values.isChecking === "true";
  const paymentDate = values.PaymentDate;
  const paymentFrequency = values.PaymentFrequency;
  const amount = values.Amount;
  const endOfMonth = values.EndOfMonth || false;

  const hasRecurringPayment = props.selectedLoan.RecurringPayment !== null;

  const submitForm = () => {
    props.setProcessStep(2);
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    props.change("NameOnAccount", props.authPayment.memberInfo.memberName);
  }, []);

  // If the amount is falsy, cancel calc post date saga and clear validation
  // to prevent previous validation from applying/showing. This is mostly
  // for the scenario when someone selects monthly frequency, has the payment amount auto-filled,
  // then selects another frequency, which clears the amount field
  useEffect(() => {
    if (!amount) {
      const paymentDetails = {};

      props.callAuthCalcPostDateV2(paymentDetails, true);

      props.clearAuthRecurringValidation();
    }
  }, [amount]);

  // Initialize from RecurringPayment loanInfo object if there is one
  useEffect(() => {
    const recurringPayment = props.selectedLoan.RecurringPayment;

    if (props.selectedLoan.RecurringPayment) {
      const initializationObject = constructRecurringInitializationObject(recurringPayment);
      props.initialize(initializationObject);
      props.searchForInstitution(recurringPayment.RoutingNumber);
    }
  }, []);

  useEffect(() => {
    const defaultMonthlyAmount = `${props.selectedLoan.RegularPaymentAmount.toFixed(2)}` || "";

    if (paymentFrequency === "Monthly" && defaultMonthlyAmount && !hasRecurringPayment) {
      props.clearAuthRecurringValidation();
      props.change("Amount", formatOlbStyleAuthAmountValueFromBackend(defaultMonthlyAmount));
    }

    if (previousFrequency.current === "Monthly" && paymentFrequency !== "Monthly") {
      props.change("Amount", "");
    }

    previousFrequency.current = paymentFrequency;
  }, [paymentFrequency]);

  // Effect to validate post date when any of the parameters change
  useEffect(() => {
    props.clearAuthRecurringValidation();
    if (shouldCallValidation()) {
      const parsedAmount = parseFloat(removeNonNumericCharacters(amount)).toFixed(2);
      const paymentDetails = {
        RoutingNumber: routingNumber,
        AccountNumber: accountNumber,
        IsChecking: checking,
        RecurringPaymentAgreementSetup: {
          PaymentDate: moment(paymentDate),
          PaymentFrequency: paymentFrequency,
          EndOfMonth: endOfMonth,
        },
        Amount: parsedAmount,
      };
      props.callAuthCalcPostDateV2(paymentDetails);
    }
  }, [
    props.achInstitution,
    accountNumber,
    checking,
    paymentDate,
    paymentFrequency,
    amount,
    endOfMonth,
  ]);

  const shouldCallValidation = () => {
    if (values) {
      return paymentDate && paymentFrequency && amount;
    }
    return false;
  };

  if (!props.thisForm || !props.thisForm.values) {
    return <div></div>;
  }

  return (
    <AuthPaymentContainer
      title="Payment Details"
      topChildren={recurringPaymentScheduledTodayMessage(
        props.PendingRecurringPaymentDate,
        props.selectedLoan.RecurringPayment,
      )}
    >
      <div className="bold f20">{generateLoanDescription(props.selectedLoan)}</div>
      <div className="f14">
        Your full monthly payment amount is {usd.format(props.selectedLoan.RegularPaymentAmount)}
      </div>
      <div className="my-4 divider"></div>
      <form onSubmit={props.handleSubmit(submitForm)}>
        <RecurringFrequency isOpenEnded={false} />
        <AuthAmountField
          isAch={true}
          isRecurring={true}
          isDebit={false}
          paymentFrequency={paymentFrequency}
          maxAllowedAchAmount={props.selectedLoan.MaxAllowedAchAmount}
          change={props.change}
        />
        <RecurringDatePicker
          show={calendarOpen}
          paymentDate={paymentDate}
          selected={props.thisForm.values.PaymentDate}
          frequency={props.thisForm.values.PaymentFrequency}
          endOfMonth={props.thisForm.values.EndOfMonth}
          setCalendarOpen={setCalendarOpen}
          change={props.change}
        />
        <AuthRecurringValidationDisplay
          recurringValidation={props.authPayment.recurringValidation}
        />
        <AuthAchFields
          thisForm={props.thisForm}
          achInstitution={props.achInstitution}
          change={props.change}
        />
        <AuthContinue
          type="submit"
          title="Continue"
          disabled={
            props.submitting ||
            paymentValidated !== true ||
            !props.achInstitution.achInstitution ||
            hasSyncErrors
          }
        />
        <AuthRestartButton backToLoans={props.backToLoans} />
      </form>
    </AuthPaymentContainer>
  );
};

//Connect to Redux store
export const AuthPaymentRecurringAchClosedEnd = reduxForm({
  form: "AuthPaymentRecurringAchClosedEnd",
  destroyOnUnmount: false,
  initialValues: {
    PaymentFrequency: "",
    PaymentDate: moment().add(1, "d").toDate(),
    Amount: "",
    isChecking: "true",
    NameOnAccount: "",
    RoutingNumber: "",
    AccountNumber: "",
    AccountNumberVerification: "",
    EndOfMonth: false,
  },
})(AuthPaymentRecurringAchClosedEndForm);



// WEBPACK FOOTER //
// ./src/components/authenticated-payments/payment-forms/auth-payment-recurring-ach-closed-end.component.js